import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import BrandLogo from '../components/BrandLogo';

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';

export default function BookDemo() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredDate: '',
    preferredTime: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(CALENDLY_URL);

  useEffect(() => {
    if (!calendlyUrl) {
      api.getCalendlyUrl().then((data) => {
        if (data.url) setCalendlyUrl(data.url);
      }).catch(() => {});
    }
  }, [calendlyUrl]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || form.name.trim().length < 2) {
      return setError('Please enter your full name');
    }
    if (!form.email.trim()) {
      return setError('Please enter your email address');
    }
    if (!form.phone.trim()) {
      return setError('Please enter your phone number');
    }
    const phoneRe = /^[+]?[\d\s\-().]{7,20}$/;
    if (!phoneRe.test(form.phone.trim())) {
      return setError('Please enter a valid phone number');
    }

    setLoading(true);
    try {
      await api.submitDemoRequest(form);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="auth-card" style={{ textAlign: 'center', maxWidth: 480 }}>
          <BrandLogo />
          <div style={{ fontSize: 48, margin: '32px 0 16px' }}>🎉</div>
          <h2 style={{ marginBottom: 8 }}>Demo Request Submitted!</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
            Thank you, <strong>{form.name}</strong>! We've sent a confirmation to <strong>{form.email}</strong>.
          </p>
          {calendlyUrl ? (
            <>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
                Book your preferred time slot now:
              </p>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary btn-block"
                style={{ marginBottom: 12, fontSize: 16, padding: '14px 24px' }}
              >
                📅 Book Your Demo Slot
              </a>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 16 }}>
                You'll be redirected to Calendly to select a time.
              </p>
            </>
          ) : (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
              Our team will reach out to schedule your personalized demo.
            </p>
          )}
          <Link to="/" className="btn btn-secondary btn-block">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div style={{ width: '100%', maxWidth: 900, display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap', padding: 20 }}>
        {/* Left - Info */}
        <div style={{ flex: '1 1 320px', color: '#fff', padding: '40px 0' }}>
          <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontSize: 24, fontWeight: 700, display: 'block', marginBottom: 24 }}>
            LoopVisitor
          </Link>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}>
            See LoopVisitor in action
          </h1>
          <p style={{ fontSize: 16, opacity: 0.9, marginBottom: 32, lineHeight: 1.6 }}>
            Get a personalized walkthrough of how LoopVisitor can help your business
            automatically bring customers back and grow reviews.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: '⏱️', text: '15-minute personalized demo' },
              { icon: '🎯', text: 'See features relevant to your industry' },
              { icon: '💬', text: 'Ask questions and get expert advice' },
              { icon: '🎁', text: 'No commitment, completely free' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, opacity: 0.9 }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right - Form */}
        <div className="auth-card" style={{ flex: '1 1 380px', maxWidth: 460 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Book a Demo</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
            Fill in your details and we'll get back to you within 24 hours.
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Full Name *</label>
              <input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="john@business.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="+1 555 123 4567"
                required
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Preferred Date</label>
                <input
                  type="date"
                  value={form.preferredDate}
                  onChange={(e) => update('preferredDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Preferred Time</label>
                <select
                  value={form.preferredTime}
                  onChange={(e) => update('preferredTime', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)' }}
                >
                  <option value="">Select time</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                  <option value="17:00">5:00 PM</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Message <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Tell us about your business and what you'd like to learn..."
                rows={3}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', resize: 'vertical' }}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Request Demo'}
            </button>
          </form>

          {calendlyUrl && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <div style={{ position: 'relative', textAlign: 'center', margin: '0 0 16px' }}>
                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid var(--border)' }} />
                <span style={{ position: 'relative', background: 'var(--surface)', padding: '0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                  or schedule directly
                </span>
              </div>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-block"
              >
                📅 Open Calendly
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
